import { useEffect, useRef, useState } from "react";
import { ChevronDown, Plus, Pencil, Trash2, Check, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PROJECT_TYPES } from "@/lib/constants";

/**
 * Campaign-type selector with persisted custom types.
 * - Lists built-in PROJECT_TYPES + the user's custom types from
 *   /api/me/project-types.
 * - "+ Add new campaign type" entry inside the dropdown opens an inline
 *   create row.
 * - "Manage your types" link opens a modal to rename / delete custom ones.
 *
 * Pure UX layer — does NOT touch agent logic, generation flow, or campaign
 * processing. Custom types pass through to the backend Project record as a
 * plain string in `project_type`, exactly the same shape as built-in values.
 */
export default function CampaignTypeSelect({ value, onChange, testid = "proj-type" }) {
    const [open, setOpen] = useState(false);
    const [custom, setCustom] = useState([]);
    const [creating, setCreating] = useState(false);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);
    const [showManage, setShowManage] = useState(false);
    const wrapRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/me/project-types");
            setCustom(Array.isArray(data) ? data : []);
        } catch {
            // best-effort — built-ins still work
        }
    };

    useEffect(() => { load(); }, []);

    // Close dropdown on outside click
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
                setCreating(false);
                setDraft("");
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const allTypes = [
        ...PROJECT_TYPES.map(t => ({ ...t, custom: false })),
        ...custom.map(t => ({ value: t.value, label: t.label, id: t.id, custom: true })),
    ];
    const selected = allTypes.find(t => t.value === value);

    const create = async () => {
        const label = draft.trim();
        if (!label) { toast.error("Type a name"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/me/project-types", { label });
            setCustom(c => [...c, data]);
            onChange(data.value);
            toast.success(`Added "${data.label}"`);
            setCreating(false);
            setDraft("");
            setOpen(false);
        } catch (e) {
            toast.error(e?.userMessage || "Could not save");
        } finally { setBusy(false); }
    };

    return (
        <div ref={wrapRef} className="relative" data-testid={testid}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                data-testid={`${testid}-button`}
                className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2.5 focus:border-zinc-500 outline-none flex items-center justify-between hover:border-zinc-700 transition-colors"
            >
                <span className="truncate">{selected?.label || "— pick a campaign type —"}</span>
                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    data-testid={`${testid}-menu`}
                    className="absolute z-30 mt-1 left-0 right-0 bg-[#0a0a0a] border border-zinc-800 rounded-sm max-h-72 overflow-y-auto scrollbar-thin shadow-xl"
                >
                    {allTypes.map(t => (
                        <button
                            key={t.value}
                            type="button"
                            data-testid={`${testid}-opt-${t.value}`}
                            onClick={() => { onChange(t.value); setOpen(false); }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-zinc-900 ${value === t.value ? "bg-zinc-900 text-white" : "text-zinc-300"}`}
                        >
                            <span className="truncate">{t.label}</span>
                            {t.custom && (
                                <span className="text-[10px] font-mono uppercase tracking-wider text-[#FF4400] ml-2 shrink-0">custom</span>
                            )}
                        </button>
                    ))}

                    {/* Inline create row */}
                    {!creating ? (
                        <button
                            type="button"
                            data-testid={`${testid}-add-new`}
                            onClick={() => { setCreating(true); setDraft(""); }}
                            className="w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 border-t border-zinc-900 text-[#FF4400] hover:bg-zinc-900"
                        >
                            <Plus className="w-3.5 h-3.5" />
                            <span>Add new campaign type</span>
                        </button>
                    ) : (
                        <div className="p-2 border-t border-zinc-900 flex items-center gap-2">
                            <input
                                autoFocus
                                data-testid={`${testid}-add-input`}
                                value={draft}
                                onChange={e => setDraft(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === "Enter") { e.preventDefault(); create(); }
                                    if (e.key === "Escape") { setCreating(false); setDraft(""); }
                                }}
                                placeholder="e.g. Festival Launch"
                                className="flex-1 bg-black border border-zinc-800 rounded-sm px-2 py-1.5 text-sm focus:border-zinc-500 outline-none"
                            />
                            <button
                                type="button"
                                data-testid={`${testid}-add-confirm`}
                                disabled={busy}
                                onClick={create}
                                className="px-2 py-1.5 bg-[#FF4400] text-white rounded-sm text-xs disabled:opacity-50"
                            >
                                <Check className="w-3.5 h-3.5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => { setCreating(false); setDraft(""); }}
                                className="px-2 py-1.5 border border-zinc-800 rounded-sm text-xs text-zinc-400"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}

                    {custom.length > 0 && (
                        <button
                            type="button"
                            data-testid={`${testid}-manage`}
                            onClick={() => { setShowManage(true); setOpen(false); }}
                            className="w-full text-left px-3 py-2 text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-white border-t border-zinc-900"
                        >
                            Manage your types →
                        </button>
                    )}
                </div>
            )}

            {showManage && (
                <ManageCustomTypesModal
                    items={custom}
                    onClose={() => setShowManage(false)}
                    onChange={(next) => setCustom(next)}
                    testid={testid}
                />
            )}
        </div>
    );
}

function ManageCustomTypesModal({ items, onClose, onChange, testid }) {
    const [editId, setEditId] = useState(null);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);

    const startEdit = (it) => { setEditId(it.id); setDraft(it.label); };
    const cancelEdit = () => { setEditId(null); setDraft(""); };

    const saveEdit = async (it) => {
        const label = draft.trim();
        if (!label) { toast.error("Name required"); return; }
        setBusy(true);
        try {
            const { data } = await api.put(`/me/project-types/${it.id}`, { label });
            onChange(items.map(x => x.id === it.id ? data : x));
            toast.success("Renamed");
            cancelEdit();
        } catch (e) {
            toast.error(e?.userMessage || "Rename failed");
        } finally { setBusy(false); }
    };

    const remove = async (it) => {
        if (!window.confirm(`Delete "${it.label}"?`)) return;
        setBusy(true);
        try {
            await api.delete(`/me/project-types/${it.id}`);
            onChange(items.filter(x => x.id !== it.id));
            toast.success("Deleted");
        } catch (e) {
            toast.error(e?.userMessage || "Delete failed");
        } finally { setBusy(false); }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid={`${testid}-manage-modal`}
            onClick={onClose}
        >
            <div
                className="card-base w-full max-w-md p-6"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="label-micro mb-1">Custom Campaign Types</div>
                        <h3 className="font-display text-xl">Manage your types</h3>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="space-y-2">
                    {items.length === 0 && (
                        <div className="text-zinc-500 text-sm">No custom types yet.</div>
                    )}
                    {items.map(it => (
                        <div
                            key={it.id}
                            data-testid={`${testid}-manage-row-${it.value}`}
                            className="flex items-center gap-2 p-2 border border-zinc-900 rounded-sm hover:border-zinc-800"
                        >
                            {editId === it.id ? (
                                <>
                                    <input
                                        autoFocus
                                        value={draft}
                                        onChange={e => setDraft(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === "Enter") { e.preventDefault(); saveEdit(it); }
                                            if (e.key === "Escape") cancelEdit();
                                        }}
                                        className="flex-1 bg-black border border-zinc-800 rounded-sm px-2 py-1.5 text-sm focus:border-zinc-500 outline-none"
                                    />
                                    <button
                                        disabled={busy}
                                        onClick={() => saveEdit(it)}
                                        className="px-2 py-1.5 bg-[#FF4400] text-white rounded-sm text-xs disabled:opacity-50"
                                    >
                                        <Check className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        onClick={cancelEdit}
                                        className="px-2 py-1.5 border border-zinc-800 rounded-sm text-xs text-zinc-400"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm truncate">{it.label}</div>
                                        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">{it.value}</div>
                                    </div>
                                    <button
                                        data-testid={`${testid}-manage-edit-${it.value}`}
                                        onClick={() => startEdit(it)}
                                        className="p-1.5 text-zinc-500 hover:text-white"
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        data-testid={`${testid}-manage-delete-${it.value}`}
                                        disabled={busy}
                                        onClick={() => remove(it)}
                                        className="p-1.5 text-zinc-500 hover:text-red-400"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </>
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex justify-end mt-6">
                    <button onClick={onClose} className="btn-secondary text-sm">
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}
